import { cn } from "../../lib/utils"
import { convertToEur, formatCurrency, type ExchangeRates, type MoneyAmount } from "../../lib/money"

type MoneyProps = {
  /** A number for computed figures, or a decimal string for exact ones — a
   *  string is formatted digit-for-digit and never round-trips through a float. */
  amount: MoneyAmount
  currency: string
  rates: ExchangeRates | undefined
  /** `inline` puts the native amount muted alongside (tables, cells); `stacked`
   *  puts it on a smaller line below (KPI cards). */
  layout?: "inline" | "stacked"
  maximumFractionDigits?: number
  className?: string
}

/** Shows an amount with EUR as the primary currency and the native amount
 *  (CZK, PLN, …) as a muted secondary. Amounts already in EUR render as a
 *  single figure. When no rate is known yet, falls back to native-only so the
 *  UI never blanks. */
export function Money({
  amount,
  currency,
  rates,
  layout = "inline",
  maximumFractionDigits = 2,
  className,
}: MoneyProps) {
  // The native figure is the authoritative one and stays exact. The EUR
  // secondary is an estimate against a floating-point rate, so — and only there
  // — a string amount is read as a number.
  const eur = convertToEur(typeof amount === "number" ? amount : Number(amount), currency, rates)

  // Already EUR, or no rate yet → a single native figure.
  if (currency === "EUR" || eur === null) {
    return (
      <span className={cn("tabular-nums", className)}>
        {formatCurrency(amount, currency, maximumFractionDigits)}
      </span>
    )
  }

  const primary = formatCurrency(eur, "EUR", maximumFractionDigits)
  const native = formatCurrency(amount, currency, maximumFractionDigits)

  if (layout === "stacked") {
    return (
      <span className={cn("flex flex-col tabular-nums", className)}>
        <span>{primary}</span>

        <span className="text-xs font-normal text-muted-foreground">{native}</span>
      </span>
    )
  }

  return (
    <span className={cn("inline-flex items-baseline gap-1.5 tabular-nums", className)}>
      <span>{primary}</span>

      <span className="text-xs text-muted-foreground">{native}</span>
    </span>
  )
}
